import React, { useState, useMemo } from "react";
import {
  Plus,
  Heart,
  Pill,
  Calendar as CalendarIcon,
  FileText,
  Syringe,
  PenTool,
  Upload,
  Calculator,
  Image as ImageIcon,
  ClipboardList,
} from "lucide-react";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { UploadDocumentModal } from "@/components/PatientDashboardV2/components/UploadDocumentModal";
import {
  PatientDocument,
  usePatientDocumentsState,
} from "@/lib/dashboard/patientDocumentsData";
import {
  getPatientConsultationByConsultationId,
  setPatientSummaryForActions,
  getPatientConsentSignature,
  getReferalLetterByFilePath,
  getPatientDocumentsByConsultation,
  getAllDestinations,
  setShowReferralLetterDialog,
} from "@/dispatchers/patientDispatchers";
import { useDocumentsData } from "@/components/Documents/documentsData";
import { ActionType } from "@/enum/ActionType";
import {
  fileToBase64,
  addDocumentFromActions,
  getDocumentTypeEnum,
} from "@/lib/dashboard/patientDocumentsData";
import {
  setShowBookingSlots,
  setBookingSlotsMode,
  setShowBacktoSlotsButton,
} from "@/dispatchers/scheduleEventsDispatchers";
import { ConsultationQRCode } from "./ConsultationQRCode";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DosageCalculator } from "../dosageCalculators/types";
import { calculatorRegistry } from "../dosageCalculators/registry";
import PermissionRequiredModal from "@/components/PermissionRequiredModal";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseCalenderUpsert,
  accessTitanversePrescriptionUpsert,
} from "@/data/constantStrings";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { UploadImageModal } from "../../UploadImageModal";
import { TreatmentPlanModal } from "../../TreatmentPlan/TreatmentPlanModal";
import {
  mockRooms,
  pharmacists as mockPharmacists,
} from "@/components/Scheduler/scheduleData";
import {
  saveTreatmentPlanDraft,
  clearTreatmentPlanDraft,
} from "@/dispatchers/treatmentPlanDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
interface AddActionPopoverProps {
  isDarkMode: boolean;
  showAddActionPopover: boolean;
  consultationId: string;
  setShowAddActionPopover: (value: boolean) => void;
  selectedPatient: any;
  companyId: string;
  setOpenPrescriptionDialog: (value: boolean) => void;
  setOpenPaymentDialog: (value: boolean) => void;
  setOpenRefDialog: (value: boolean) => void;
  setOpenBookingDialog: (value: boolean) => void;
  setShowAddVitalsModal: (value: boolean) => void;
  setOpenVaccineDialog: (value: boolean) => void;
  setOpenSignatureDialog: (value: any) => void;
  setShowSummaryDocument: (value: boolean) => void;
  setDynamicPrescriptions: (value: any) => void;
  setPrescriptionId: (value: string) => void;
  setIsPrescriptionUpdate: (value: boolean) => void;
  setCurrentOpenVitalsModal: (value: any) => void;
  setVaccineDetails: (value: any) => void;
  setLetterDetails: (value: any) => void;
  setReferralLetter: (value: any) => void;
  setBookingDetails: (value: any) => void;
  setIsChatReferralLetter: (value: boolean) => void;
  onActionComplete?: (actionType: string, data: any) => void;
  setIncrementPrescriptionCompleted: (value: boolean) => void;
  setIncrementVaccineCompleted: (value: boolean) => void;
  onSelectCalculator?: (calculator: DosageCalculator) => void;
  consultationCompletedActions?: any;
  setShowAddActionButton?: (value: boolean) => void;
}

export const AddActionPopover: React.FC<AddActionPopoverProps> = ({
  isDarkMode,
  showAddActionPopover,
  consultationId,
  setShowAddActionPopover,
  selectedPatient,
  companyId,
  setOpenPrescriptionDialog,
  setOpenPaymentDialog,
  setOpenRefDialog,
  setOpenBookingDialog,
  setShowAddVitalsModal,
  setOpenVaccineDialog,
  setOpenSignatureDialog,
  setShowSummaryDocument,
  setDynamicPrescriptions,
  setPrescriptionId,
  setIsPrescriptionUpdate,
  setCurrentOpenVitalsModal,
  setVaccineDetails,
  setLetterDetails,
  setReferralLetter,
  setBookingDetails,
  setIsChatReferralLetter,
  onActionComplete,
  setIncrementPrescriptionCompleted,
  setIncrementVaccineCompleted,
  onSelectCalculator,
  setShowAddActionButton,
  consultationCompletedActions,
}) => {
  const { patientState, appState } = useTitanApp();
  const { features } = appState || {};
  const { ifTreatmentPlanEnabled } = features;
  const { patientAIChatMessages, consultationData, isConsultationDataLoading } = patientState;

  const treatmentPlanState = useSelector(
    (state: any) => state.treatmentPlan || {},
  );
  const draftData = useMemo(() => {
    if (!consultationId || !treatmentPlanState.treatmentPlanDrafts) {
      return null;
    }
    const draft = treatmentPlanState.treatmentPlanDrafts[consultationId];
    return draft ? draft.data : null;
  }, [consultationId, treatmentPlanState.treatmentPlanDrafts]);

  const isTreatmentPlanDisabled = useMemo(() => {
  
    const hasLinkedPlan = !!treatmentPlanState.linkedTreatmentPlanId;
    const hasPlanInActions =
      patientAIChatMessages?.some(
        (msg: any) =>
          msg?.sender === "success" && String(msg?.actionId) === "15",
      ) ||
      (consultationData?.actions || []).some(
        (action: any) =>
          action?.actionName === "CreateTreatmentPlan" ||
          action?.actionName === "UpdateTreatmentPlan",
      );
    return hasLinkedPlan || hasPlanInActions;
  }, [treatmentPlanState.linkedTreatmentPlanId, patientState]);
  // ──────────────────────────────────────────────────────────────────────
  const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [uploadImageModalOpen, setUploadImageModalOpen] = useState(false);
  const {
    getCompanyDocumentsByCompanyId,
    updateConsultationActions,
    setAddPatientAIChatMessage,
    SaveAIChatMessage,
    ifCalculatorAcitonsEnabled,
  } = useDocumentsData();
  const { summaryDocumentId } = usePatientDocumentsState();
  const hasAppointmentPermission = checkUserPermissions(
    accessTitanverseCalenderUpsert,
  );
  const hasPrescriptionPermission = checkUserPermissions(
    accessTitanversePrescriptionUpsert,
  );
  const handleActionSave = async (payload) => {
    await updateConsultationActions({
      consultationId: consultationId,
      payload: payload,
    });

    const documentMessage = [
      {
        sender: "success",
        message: "Document uploaded successfully.",
        actionId: 8,
        details: payload,
      },
    ];
    await SaveAIChatMessage({
      ConsultationChat: JSON.stringify(documentMessage),
      ConsultationId: consultationId,
    });

    setAddPatientAIChatMessage({
      sender: "success",
      message: "Document uploaded successfully.",
      actionId: 8,
      details: payload,
    });
  };

  const handleUploadDocument = async (documentData) => {
    try {
      setIsUploadingDocument(true);
      const patientId = String(
        selectedPatient?.patientID ||
          selectedPatient?.patientId ||
          selectedPatient?.id ||
          "",
      );

      if (!patientId) {
        console.error("Patient ID not found");
        setIsUploadingDocument(false);
        return;
      }
      if (
        documentData.libraryDocId != null &&
        documentData.libraryDocId != undefined
      ) {
        {
          const documentPayload = {
            payload: JSON.stringify({
              libraryDocId: documentData.libraryDocId,
              libraryDocTitle: documentData.title,
              libraryDocFileName: documentData.mainFile,
              recordedAt: new Date().toISOString(),
            }),
            actionName: "LibraryDocument",
            actionNumber: "8",
            isPerformed: true,
          };
          handleActionSave(documentPayload);
        }
      } else {
        const { base64File, contentType } = await fileToBase64(
          documentData.file,
        );
        const backendDoc = {
          patientId: patientId,
          title: documentData.customName,
          description: `Uploaded ${documentData?.type?.toLowerCase()} document`,
          size: `${Math.round(documentData.file.size / 1024)} KB`,
          status: 1, // Available
          fileContent: base64File,
          contentType: contentType,
          type: getDocumentTypeEnum(documentData.type),
          consultationId: consultationId,
        };

        const apiResponse = await addDocumentFromActions(backendDoc);

        if (apiResponse) {
          const documentId = apiResponse;
          const { fileContent, ...cleanBackendDoc } = backendDoc;
          const documentPayload = {
            payload: JSON.stringify({
              payload: { ...cleanBackendDoc, documentId: documentId },
              recordedAt: new Date().toISOString(),
            }),
            actionName: ActionType[8],
            actionNumber: "8",
            isPerformed: true,
          };
          handleActionSave(documentPayload);
        }
      }

      // Track as completed action
      if (onActionComplete) {
        onActionComplete("document", {
          fileName: documentData.customName,
          documentType: documentData.type,
          timestamp: new Date(),
        });
      }

      setIsUploadingDocument(false);
      setUploadDocumentModalOpen(false);
      setShowAddActionPopover(false);
    } catch (error) {
      console.error("Error uploading document:", error);
      setIsUploadingDocument(false);
    }
  };

  const handleOpenSummaryDocument = async () => {
    setShowSummaryDocument(true);
    setShowAddActionPopover(false);
    setPatientSummaryForActions("");
    const obj = {
      consultationId: consultationId,
      patientId: selectedPatient?.patientID || selectedPatient?.patientId,
    };
    await getPatientDocumentsByConsultation(consultationId);
    await getReferalLetterByFilePath(obj);
    const latestSignatureAction = consultationCompletedActions
      ?.filter((action) => action.actionName === "Signature")
      ?.map((action) => {
        let payload = null;
        try {
          payload = action?.payload ? JSON.parse(action.payload) : null;
        } catch (error) {
          console.warn("Failed to parse signature payload", error);
        }
        const signedAtTs = payload?.signedAt
          ? new Date(payload.signedAt).getTime()
          : Number.NEGATIVE_INFINITY;
        return { action, payload, signedAtTs };
      })
      ?.sort((a, b) => b.signedAtTs - a.signedAtTs)?.[0];

    const latestSignatureId = latestSignatureAction?.payload?.signatureId;

    if (latestSignatureId) {
      const payload = {
        consultationId,
        signatureId: latestSignatureId,
      };
      await getPatientConsentSignature(payload);
    }
    await getPatientConsultationByConsultationId(consultationId);
  };

  // --------------------Static data---------------------------------------------------------------------------------
  const allCalculators = Object.values(calculatorRegistry);
  const handleSelectCalculator = (calculator: DosageCalculator) => {
    setShowAddActionButton(false);
    if (calculator.id === "travelHealth") {
      getAllDestinations();
    }
    if (onSelectCalculator) {
      onSelectCalculator(calculator);
    }
    setShowAddActionPopover(false);
  };

  return (
    <div
      className={`p-4 border-t ${
        isDarkMode ? "border-gray-700" : "border-gray-200"
      }`}
    >
      <Popover
        open={showAddActionPopover}
        onOpenChange={setShowAddActionPopover}
      >
        <PopoverTrigger asChild>
          <Button
            className={`w-full ${
              isDarkMode
                ? "bg-black hover:bg-gray-900 text-white"
                : "bg-black hover:bg-gray-900 text-white"
            }`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add action
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={`w-96 p-0 ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
          align="end"
          side="top"
          sideOffset={8}
        >
          {/* QR Code Section */}
          {/* <div className="p-3 pb-2">
            <ConsultationQRCode
              consultationId={`$TV${consultationId}`}
              isDarkMode={isDarkMode}
              variant="full"
            />
          </div> */}

          <Tabs defaultValue="actions" className="w-full">
            <div
              className={`px-3 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <TabsList className="justify-start w-full">
                <TabsTrigger value="actions">Actions</TabsTrigger>
                {ifCalculatorAcitonsEnabled && (
                  <TabsTrigger value="calculators" disabled={isConsultationDataLoading}>
                    <Calculator className="w-4 h-4" />
                    Calculators
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Actions Tab */}
            <TabsContent value="actions" className="p-3 mt-0">
              <div className="overflow-y-auto max-h-80">
                <div className="grid grid-cols-3 gap-2 overflow-y-auto max-h-80">
                  {/* Prescription */}
                  <button
                    onClick={() => {
                      if (!hasPrescriptionPermission) {
                        setShowPermissionMessage(
                          "Create or update prescriptions",
                        );
                        setShowPermissionModal(true);
                        return;
                      }

                      setOpenPrescriptionDialog(true);
                      setDynamicPrescriptions([
                        {
                          id: Date.now(),
                          value: "",
                          quantity: "",
                          dose: "",
                          notes: "",
                          prescribedItemId: "",
                        },
                      ]);
                      setPrescriptionId("");
                      setIsPrescriptionUpdate(false);
                      setShowAddActionPopover(false);
                      setIncrementPrescriptionCompleted(false);
                    }}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    } ${
                      !hasPrescriptionPermission
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <Pill
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Prescription</span>
                  </button>

                  {/* Payment */}
                  {/* <button
                onClick={() => {
                  setOpenPaymentDialog(true);
                  setShowAddActionPopover(false);
                }}
                className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                }`}
              >
                <Heart
                  className={`w-5 h-5 ${
                    isDarkMode ? "text-white" : "text-black"
                  }`}
                />
                <span className="text-xs text-center">Payment</span>
              </button> */}

                  {/* Referral */}
                  <button
                    onClick={() => {
                      setShowReferralLetterDialog(true);
                      // setOpenRefDialog(true);
                      // setLetterDetails(null);
                      // setReferralLetter(null);
                      // setIsChatReferralLetter(false);
                      // setShowAddActionPopover(false);
                    }}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    }`}
                  >
                    <FileText
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Doc Template</span>
                  </button>

                  {/* Appointment */}
                  <button
                    onClick={() => {
                      if (!hasAppointmentPermission) {
                        setShowPermissionMessage(
                          "Create or update calender events",
                        );
                        setShowPermissionModal(true);

                        return;
                      }

                      setBookingSlotsMode("fromAppointment");
                      setShowBacktoSlotsButton(true);
                      setShowBookingSlots(true);
                      setBookingDetails(null);
                      setShowAddActionPopover(false);
                    }}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    } ${
                      !hasAppointmentPermission
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <CalendarIcon
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Appointment</span>
                  </button>

                  {/* Vitals */}
                  <button
                    onClick={() => {
                      setShowAddVitalsModal(true);
                      setCurrentOpenVitalsModal({ isEdit: false });
                      setShowAddActionPopover(false);
                    }}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    }`}
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Vitals</span>
                  </button>

                  {/* Vaccine */}
                  <button
                    onClick={() => {
                      setVaccineDetails(null);
                      setOpenVaccineDialog(true);
                      setShowAddActionPopover(false);
                      setIncrementVaccineCompleted(false);
                    }}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    }`}
                  >
                    <Syringe
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Vaccine</span>
                  </button>

                  {/* Patient Signature */}
                  <button
                    onClick={() => {
                      setOpenSignatureDialog({
                        open: true,
                        viewMode: false,
                        details: null,
                      });
                      setShowAddActionPopover(false);
                    }}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    }`}
                  >
                    <PenTool
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Signature</span>
                  </button>

                  {/* Document */}
                  <button
                    onClick={async () => {
                      setUploadDocumentModalOpen(true);
                      setShowAddActionPopover(false);
                      await getCompanyDocumentsByCompanyId(companyId);
                    }}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    }`}
                  >
                    <Upload
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Document</span>
                  </button>

                  {/* Summary Document */}
                  <button
                    onClick={handleOpenSummaryDocument}
                    className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                    }`}
                  >
                    <FileText
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    />
                    <span className="text-xs text-center">Summary</span>
                  </button>
                  {ifTreatmentPlanEnabled && (
                    <TreatmentPlanModal
                      trigger={
                        <button
                          disabled={isTreatmentPlanDisabled}
                          onClick={(e) => {
                            if (isTreatmentPlanDisabled) e.preventDefault();
                          }}
                          title={
                            isTreatmentPlanDisabled
                              ? "A treatment plan is already linked or created for this consultation"
                              : undefined
                          }
                          className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                            isDarkMode
                              ? "bg-gray-700 text-white"
                              : "bg-white text-gray-900 border border-gray-200"
                          } ${
                            isTreatmentPlanDisabled
                              ? "opacity-40 cursor-not-allowed"
                              : isDarkMode
                                ? "hover:bg-gray-600"
                                : "hover:bg-gray-50"
                          }`}
                        >
                          <ClipboardList
                            className={`w-5 h-5 ${isDarkMode ? "text-white" : "text-black"}`}
                          />
                          <span className="text-xs text-center">
                            Treatment Plan
                          </span>
                        </button>
                      }
                      selectedPatient={selectedPatient}
                      consultationId={consultationId}
                      existingPrescriptions={[]}
                      existingAppointments={[]}
                      schedulePharmacists={mockPharmacists}
                      scheduleRoomNames={mockRooms}
                      isDarkMode={isDarkMode}
                      draftData={draftData}
                      onSave={(data) => {
                        clearTreatmentPlanDraft(consultationId);
                        setShowAddActionPopover(false);
                        if (onActionComplete)
                          onActionComplete("treatmentPlan", data);
                      }}
                      onSaveDraft={(data) => {
                        saveTreatmentPlanDraft(consultationId, data);
                      }}
                      fromConsultationActions={true}
                    />
                  )}
                  <UploadImageModal
                    consultationId={consultationId}
                    patientId={
                      selectedPatient?.patientID ||
                      selectedPatient?.patientId ||
                      selectedPatient?.id ||
                      ""
                    }
                    mode="consultation"
                    trigger={
                      <button
                        className={`w-full flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? "bg-gray-700 hover:bg-gray-600 text-white"
                            : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
                        }`}
                      >
                        <ImageIcon
                          className={`w-5 h-5 ${isDarkMode ? "text-white" : "text-black"}`}
                        />
                        <span className="text-xs text-center">Image</span>
                      </button>
                    }
                    onSave={(imageData) => {
                      if (onActionComplete) {
                        onActionComplete("image", imageData);
                      }
                    }}
                    patientName={
                      `${selectedPatient?.foreName || ""} ${selectedPatient?.surName || ""}`.trim() ||
                      selectedPatient?.patientName ||
                      ""
                    }
                    patientEmail={selectedPatient?.email || ""}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Calculators Tab */}
            <TabsContent value="calculators" className="p-3 mt-0">
              <div className="grid grid-cols-2 gap-2 overflow-y-auto max-h-80">
                {allCalculators.map((calculator) => (
                  <button
                    key={calculator.id}
                    onClick={() => handleSelectCalculator(calculator)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg transition-colors text-left ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-white hover:bg-purple-50 text-gray-900 border border-gray-200 hover:border-purple-300"
                    }`}
                  >
                    <Calculator
                      className={`w-6 h-6 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`}
                    />
                    <span className="text-xs font-medium text-center">
                      {calculator.name.replace(" Calculator", "")}
                    </span>
                  </button>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>

      {/* Upload Document Modal */}
      <UploadDocumentModal
        open={uploadDocumentModalOpen}
        onOpenChange={setUploadDocumentModalOpen}
        isDarkMode={isDarkMode}
        onUploadDocument={handleUploadDocument}
        isLoading={isUploadingDocument}
      />
    </div>
  );
};
